import { Package, ShoppingCart, Layers, MessageSquare, FileText, ClipboardList, Lock, PlayCircle, PenLine, Sparkles } from "lucide-react";

/** Aperçu en direct de l'app formateur / espace stagiaire / connexion / panel partenaire selon la config Builder. */
export function BuilderPreview({ config, variant = "student", registry }) {
  const b = config?.branding || {};
  const f = config?.features || {};
  const primary = b.primary || "#f7c935";
  const bg = b.background || "#0b0811";
  const accent = b.accent || "#38bdf8";
  const name = b.app_name || "Blitzz Formation";

  const Logo = () => b.logo_url
    ? <img src={b.logo_url} alt={name} className="h-8 max-w-[120px] object-contain" />
    : <div className="h-8 px-3 rounded-lg flex items-center text-xs font-black tracking-tight" style={{ background: primary, color: "#14101c" }}>{name.slice(0, 18)}</div>;

  const Tab = ({ label, on = true }) => (
    <span className={"text-[10px] font-bold px-2 py-1 rounded " + (on ? "" : "line-through opacity-40")} style={{ color: on ? primary : "#94a3b8", background: on ? primary + "22" : "transparent" }}>{label}</span>
  );

  if (variant === "login") {
    return (
      <div className="rounded-2xl overflow-hidden border border-white/10 min-h-[420px] flex" style={{ background: bg }} data-testid="builder-preview-login">
        <div className="hidden md:flex w-1/2 flex-col justify-end p-6 relative" style={{ background: b.cover_url ? `url(${b.cover_url}) center/cover` : `radial-gradient(circle at 30% 20%, ${primary}66, transparent 55%), ${bg}` }}>
          <div className="text-white text-2xl font-black leading-tight">{b.welcome_title || "Bienvenue"}</div>
          <div className="text-slate-300 text-xs mt-2 max-w-xs">{b.welcome_text}</div>
        </div>
        <div className="flex-1 p-6 flex flex-col justify-center gap-3">
          <Logo />
          <div className="text-white font-bold text-base mt-2">Connexion</div>
          <div className="text-[11px] text-slate-400">{b.login_tagline}</div>
          <div className="h-9 rounded-lg bg-white/5 border border-white/10" />
          <div className="h-9 rounded-lg bg-white/5 border border-white/10" />
          <div className="h-9 rounded-lg flex items-center justify-center text-xs font-black" style={{ background: primary, color: "#14101c" }}>Se connecter</div>
          {b.footer_text && <div className="text-[10px] text-slate-500 mt-2">{b.footer_text}</div>}
        </div>
      </div>
    );
  }

  if (variant === "partner") {
    const types = registry?.partner_types || [];
    return (
      <div className="rounded-2xl overflow-hidden border border-white/10 min-h-[420px]" style={{ background: bg }} data-testid="builder-preview-partner">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10" style={{ background: b.secondary || "#1f2937" }}>
          <Logo />
          <span className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Panel partenaire</span>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          {types.map((t) => {
            const mods = (registry?.partner_modules?.[t.key] || []).filter((m) => (config?.partner_access?.[t.key] || {})[m.key] !== false);
            return (
              <div key={t.key} className="rounded-xl border border-white/10 p-3 bg-white/[0.03]">
                <div className="text-xs font-black text-white mb-1">{t.label}</div>
                <div className="flex flex-wrap gap-1">
                  {mods.length === 0 ? <span className="text-[10px] text-slate-500 italic">Aucun module</span> : mods.map((m) => <Tab key={m.key} label={m.label} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const isTrainer = variant === "trainer";
  return (
    <div className="rounded-2xl overflow-hidden border border-white/10 min-h-[420px]" style={{ background: bg }} data-testid={"builder-preview-" + variant}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <Logo />
        <div className="flex gap-2 text-[10px] text-slate-400"><span>{isTrainer ? "Espace formateur" : "Espace stagiaire"}</span></div>
      </div>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-2" style={{ color: primary }}>{name}</div>
        <div className="text-2xl font-black text-white leading-tight">{b.welcome_title || "Bienvenue"}</div>
        <p className="text-xs text-slate-400 mt-1 max-w-md">{b.welcome_text}</p>
        <div className="flex flex-wrap gap-1.5 mt-4">
          <Tab label="Cours" />
          {isTrainer && <Tab label="Stagiaires" />}
          {!isTrainer && <Tab label="Documents" on={f.documents !== false} />}
          {!isTrainer && <Tab label="Évaluations" on={f.evaluations !== false} />}
          <Tab label="Messagerie" on={f.messagerie !== false} />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="col-span-1 rounded-xl border border-white/10 p-3 space-y-2 bg-white/[0.02]">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] text-slate-300">
                {f.deverrouillage !== false && i === 3 ? <Lock className="w-3 h-3 text-slate-500" /> : <PlayCircle className="w-3 h-3" style={{ color: accent }} />}
                <span className={i === 3 && f.deverrouillage !== false ? "text-slate-500" : ""}>Module {i}</span>
                {f.scorm_only && <span className="ml-auto text-[8px] font-bold px-1 rounded" style={{ background: accent + "33", color: accent }}>SCORM</span>}
              </div>
            ))}
          </div>
          <div className="col-span-2 rounded-xl border border-white/10 p-3 bg-white/[0.02] flex flex-col gap-2">
            <div className="h-24 rounded-lg bg-white/5 flex items-center justify-center text-[10px] text-slate-500"><Package className="w-4 h-4 mr-1" /> {f.projection !== false && isTrainer ? "Projection plein écran" : "Contenu du module"}</div>
            <div className="flex flex-wrap gap-1.5 mt-auto">
              {isTrainer && <Tab label="Émargement" on={f.emargement !== false} />}
              {isTrainer && <Tab label="Déverrouiller" on={f.deverrouillage !== false} />}
              {!isTrainer && <Tab label="Progression" on={f.progression !== false} />}
              <Tab label="Hors ligne" on={f.offline !== false} />
              {f.ai_assistant && <Tab label="Assistant IA" />}
            </div>
          </div>
        </div>
        {b.footer_text && <div className="text-[10px] text-slate-500 mt-4">{b.footer_text}</div>}
      </div>
    </div>
  );
}

export const PREVIEW_VARIANTS = [
  { key: "trainer", label: "App formateur", icon: PlayCircle },
  { key: "student", label: "Espace stagiaire", icon: Layers },
  { key: "login", label: "Connexion", icon: PenLine },
  { key: "partner", label: "Panel partenaire", icon: ShoppingCart },
];

export const UNUSED_ICONS = { MessageSquare, FileText, ClipboardList, Sparkles };
